import PropTypes from 'prop-types'
import {useEffect} from 'react'

import {
  RateShape,
  BulkRateShape,
  RateErrorShape,
} from '../../common/PropTypes.js'
import {showMultiboxModal} from '../LabelConfig/Modals/MultiboxModal/index.js'
import ButtonRefresh from '../../common/components/Button/ButtonRefresh.js'
import {formatMetaClassName} from './Fields/useLabelProperty.js'
import RatePopOut from './RatePopOut.js'
import useStable from '../../common/useStable.js'
import {useLabelConfigContext} from './LabelConfigContext.js'
import {
  isMultiboxSelector,
  isSimpleRateBoxShapeSelector,
  LABEL_PROPERTY_PRESERVE_RATE_SELECTION,
  labelConfigSelector,
  labelShipperTypeSelector,
  multiboxShipperTypesSelector,
  selectedRateSelector,
  setPreserveRateSelection,
} from '../../data/labelInfos/index.js'
import {getState, useSelector} from '../../store.js'
import {usePresetMultiShipperSelector} from '../../data/company.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import Icon from '../../common/icons/Icon.js'
import className from '../../common/className.js'

function isDisabled(rate) {
  return rate.disabled || !rate.key
}

export default function RateSelection({
  value,
  hasErrors,
  hasWarnings,
  updated,
  loading,
  disabled,
  packageCount,
  canHaveMultibox,
  rates,
  refresh,
  onChange,
  currencySymbol,
}) {
  const {labelInfoID} = useLabelConfigContext()
  const disabledRates = rates.filter((rate) => isDisabled(rate))
  const notDisabledRates = useStable(rates.filter((rate) => !isDisabled(rate)))
  const usePresetMultiShipper = useSelector(usePresetMultiShipperSelector)
  const config = useSelector((state) =>
    labelConfigSelector(state, {labelInfoID}),
  )
  const preserveRateSelection =
    config[LABEL_PROPERTY_PRESERVE_RATE_SELECTION] !== false

  const rate = useSelector((state) =>
    selectedRateSelector(state, {labelInfoID}),
  )
  const shipperType = useSelector((state) =>
    labelShipperTypeSelector(state, {labelInfoID}),
  )
  const isSimpleRateBoxShape = useSelector((state) =>
    isSimpleRateBoxShapeSelector(state, {labelInfoID, shipperType}),
  )
  const isMultibox = useSelector((state) =>
    isMultiboxSelector(state, {labelInfoID, shipperType}),
  )
  const disableMultibox = !isMultibox && isSimpleRateBoxShape

  const hasNoRates = !loading && !disabled && rates.length === 0

  const hasOnlyDisabledRates =
    rates.length === disabledRates.length && rates.length > 0

  useEffect(() => {
    if (!rate && notDisabledRates.length) {
      if (!usePresetMultiShipper) {
        onChange(notDisabledRates[0])
      }
    } else if (value && hasOnlyDisabledRates) {
      onChange(null)
    }
  }, [
    hasOnlyDisabledRates,
    value,
    notDisabledRates,
    rate,
    usePresetMultiShipper,
  ])

  return (
    <div className={formatMetaClassName('rate_select')}>
      <label className="label--bold flex--justify" htmlFor="rate-select">
        <span>
          <span>
            Shipping Methods/Rates<span className="required">*</span>
          </span>
          {updated && (
            <span className="label__callout label__callout--blue margin-left-5">
              Updated
            </span>
          )}
        </span>
        {usePresetMultiShipper && (
          <ButtonLink
            className={className`no-underline tooltip--left ${{
              'preserve--on': preserveRateSelection,
              'preserve--off': !preserveRateSelection,
            }}`}
            data-hint={
              preserveRateSelection
                ? 'Selected shipping method will be preserved.'
                : 'Enable to preserve selected shipping method.'
            }
            onClick={(event) => {
              // Blur so the :focus tooltip doesn't stay stuck open after a mouse click
              event.currentTarget.blur()
              setPreserveRateSelection(labelInfoID, !preserveRateSelection)
            }}
          >
            <Icon
              name={preserveRateSelection ? 'bookmark' : 'bookmark-ol'}
              className="fs-02"
            />
          </ButtonLink>
        )}
      </label>
      <div className="flex">
        <RatePopOut
          value={value}
          onChange={(rate) => onChange(rate)}
          rates={rates}
          updated={updated}
          loading={loading}
          disabled={disabled}
          hasErrors={hasErrors}
          hasWarnings={hasWarnings}
          hasOnlyDisabledRates={hasOnlyDisabledRates}
          hasNoRates={hasNoRates}
          currencySymbol={currencySymbol}
        />
        <div className="flex align-items-center">
          <ButtonRefresh
            title="Refresh rates"
            loading={loading}
            onClick={refresh}
            className="meta-labelconfigform-button-refreshrates"
          />
          {canHaveMultibox && (
            <button
              className={className`btn btn--primary btn--primary-ol btn--multibox btn--multibox-rs meta-labelconfigform-button-multibox ${{
                'btn--disabled': loading,
              }}`}
              type="button"
              title={
                disableMultibox
                  ? 'UPS Simple Rates do not support multiple packages'
                  : 'Add multiple packages to this label'
              }
              onClick={() => {
                const [shipperType] = multiboxShipperTypesSelector(getState(), {
                  labelInfoID,
                })

                showMultiboxModal(labelInfoID, shipperType)
              }}
              disabled={disableMultibox}
            >
              {packageCount === 1 && (
                <span className="text--multibox fs-02">+</span>
              )}
              {packageCount > 1 && (
                <strong className="text--multibox fs-00 lh-lg">
                  {packageCount}
                </strong>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

RateSelection.propTypes = {
  rates: PropTypes.arrayOf(
    PropTypes.oneOfType([RateShape, RateErrorShape, BulkRateShape]),
  ),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,

  hasErrors: PropTypes.bool,
  hasWarnings: PropTypes.bool,
  updated: PropTypes.bool,
  loading: PropTypes.bool,
  packageCount: PropTypes.number,
  canHaveMultibox: PropTypes.bool,

  disabled: PropTypes.bool,

  currencySymbol: PropTypes.string.isRequired,
}
